import { useT, useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    intro:
      "Fünf Schritte. Klar dokumentiert. Bei jedem Auftrag identisch — damit Qualität und Termin keine Versprechen bleiben, sondern messbar eingehalten werden.",
    step: "Schritt",
    steps: [
      { n: "01", title: "Anfrage", body: "Senden Sie uns Ihre Bauteildaten, gewünschtes Verfahren und Ihren Zeitplan — telefonisch oder über das Offerte-Formular." },
      { n: "02", title: "Beratung & Planung", body: "Wir prüfen Geometrie, Werkstoff und Stückzahl, schlagen das passende Verfahren vor und stellen die Offerte zusammen." },
      { n: "03", title: "Veredelung", body: "Ausführung in Mels oder am Partnerstandort — auf Swiss Safety Center zertifizierten Anlagen mit überwachter Prozesschemie." },
      { n: "04", title: "Qualitätsprüfung", body: "Optische und messtechnische Prüfung jeder Charge — Schichtdicken, Haftung, Oberflächenqualität, dokumentiert." },
      { n: "05", title: "Lieferung", body: "Termingerechte Auslieferung über unsere national und international ausgelegte Logistik. Qualität und Termin — wie versprochen." },
    ],
  },
  en: {
    intro:
      "Five steps. Clearly documented. Identical for every order — so that quality and on-time delivery aren't promises, but measurable outcomes.",
    step: "Step",
    steps: [
      { n: "01", title: "Inquiry", body: "Send us your part data, desired process and timeline — by phone or via the quote form." },
      { n: "02", title: "Consultation & planning", body: "We review geometry, material and quantity, suggest the right process and prepare the quote." },
      { n: "03", title: "Finishing", body: "Execution in Mels or at the partner site — on Swiss Safety Center certified equipment with monitored process chemistry." },
      { n: "04", title: "Quality control", body: "Visual and metrological inspection of every batch — coating thickness, adhesion, surface quality, documented." },
      { n: "05", title: "Delivery", body: "On-time shipment via our domestic and international logistics. Quality and on-time — as promised." },
    ],
  },
  fr: {
    intro:
      "Cinq étapes. Clairement documentées. Identiques pour chaque commande — afin que la qualité et les délais ne soient pas des promesses, mais des résultats mesurables.",
    step: "Étape",
    steps: [
      { n: "01", title: "Demande", body: "Envoyez-nous vos données de pièce, le procédé souhaité et votre planning — par téléphone ou via le formulaire de devis." },
      { n: "02", title: "Conseil & planification", body: "Nous étudions la géométrie, le matériau et la quantité, proposons le procédé adapté et établissons l'offre." },
      { n: "03", title: "Traitement", body: "Réalisation à Mels ou chez le site partenaire — sur installations certifiées Swiss Safety Center avec chimie de procédé contrôlée." },
      { n: "04", title: "Contrôle qualité", body: "Contrôle visuel et métrologique de chaque lot — épaisseur de couche, adhérence, qualité de surface, documenté." },
      { n: "05", title: "Livraison", body: "Expédition dans les délais via notre logistique nationale et internationale. Qualité et délais — comme promis." },
    ],
  },
  it: {
    intro:
      "Cinque fasi. Chiaramente documentate. Identiche per ogni ordine — affinché qualità e puntualità non siano promesse, ma risultati misurabili.",
    step: "Fase",
    steps: [
      { n: "01", title: "Richiesta", body: "Inviateci i dati del componente, il procedimento desiderato e i tempi — telefonicamente o tramite il modulo offerta." },
      { n: "02", title: "Consulenza & pianificazione", body: "Esaminiamo geometria, materiale e quantità, proponiamo il procedimento adatto e prepariamo l'offerta." },
      { n: "03", title: "Trattamento", body: "Esecuzione a Mels o presso la sede partner — su impianti certificati Swiss Safety Center con chimica di processo monitorata." },
      { n: "04", title: "Controllo qualità", body: "Verifica visiva e metrologica di ogni lotto — spessore, adesione, qualità superficiale, documentato." },
      { n: "05", title: "Consegna", body: "Spedizione puntuale attraverso la nostra logistica nazionale e internazionale. Qualità e puntualità — come promesso." },
    ],
  },
};

export const Process = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="ablauf"
      data-testid="process-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-5">
            <div className="overline reveal mb-5">{t("sections.ablauf_overline", "— Ablauf")} · 05 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.ablauf_title_1", "Von der Anfrage")}
              <br />
              <span className="text-zinc-500">{t("sections.ablauf_title_2", "bis zur Auslieferung.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-[22px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />

          <ol className="space-y-14 md:space-y-24">
            {c.steps.map((s, i) => {
              const isLeft = i % 2 === 0;
              return (
                <li
                  key={s.n}
                  data-testid={`process-step-${s.n}`}
                  className="relative grid grid-cols-1 md:grid-cols-2 items-center gap-8"
                >
                  <div className="absolute left-[14px] md:left-1/2 -translate-x-1/2 top-1 md:top-1/2 md:-translate-y-1/2">
                    <div className="w-4 h-4 rounded-full bg-white pulse-dot ring-4 ring-[#0B0B0D]" />
                  </div>

                  <div
                    className={`reveal reveal-delay-${(i % 5) + 1} pl-12 md:pl-0 ${
                      isLeft ? "md:pr-20 md:text-right" : "md:col-start-2 md:pl-20"
                    }`}
                  >
                    <div className="overline mb-3">{c.step} {s.n}</div>
                    <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-white mb-3">
                      {s.title}
                    </h3>
                    <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-md md:inline-block">
                      {s.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default Process;
